import * as React from 'react'
import Select, { ValueType, components } from 'react-select'
import { FormGroup, Label } from 'reactstrap'
import intl from 'react-intl-universal'
import { useDispatch, useSelector } from 'react-redux'

import PaywallStatusConfirmModal from '~/reactapp/App/PaywallStatusConfirmModal'
import { setStoryAns } from '~/redux/storyData'
import { storyLockedAccessor } from '~/redux/appReducer'
import { hasPermission } from '~/modules/services/permission'
import { EllipsisReduxStore, Website } from '~/types'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./PrimaryWebsiteField.scss')

type OptionType = { label: string; value: string }

function generateOptions(
  websites: { [websiteId: string]: Website },
  permissionFilter?: 'EDPW' | 'CRES'
): OptionType[] {
  let filteredWebsites = Object.values(websites)

  if (permissionFilter === 'CRES') {
    filteredWebsites = filteredWebsites.filter((website) =>
      hasPermission(window.userRolesAuth.story.createStory, website._id)
    )
  } else if (permissionFilter === 'EDPW') {
    filteredWebsites = filteredWebsites.filter((website) =>
      hasPermission(window.userRolesAuth.story.editPrimaryWebsite, website._id)
    )
  }

  return filteredWebsites.map((website) => ({
    label: website.display_name,
    value: website._id,
  }))
}

const SelectContainer: typeof components['SelectContainer'] = ({
  children,
  ...props
}) => {
  return (
    <div
      data-testid="custom-select-container"
      data-tooltip={
        !props.selectProps.selectProps.canChangePrimaryWebsite
          ? intl.get('PrimaryWebsiteField.missingEditPrimaryWebsiteTooltip')
          : ''
      }
    >
      <components.SelectContainer {...props}>
        {children}
      </components.SelectContainer>
    </div>
  )
}

export const PrimaryWebsiteField: React.FC = () => {
  const dispatch = useDispatch()
  const story = useSelector(
    (state: EllipsisReduxStore) => state.storyData.story
  )
  const isStoryLocked = useSelector((state: EllipsisReduxStore) =>
    storyLockedAccessor(state)
  )
  const websites = useSelector((state: EllipsisReduxStore) => state.websites)
  const isEditingTemplate = useSelector(
    (state: EllipsisReduxStore) => state.route.current === '/template'
  )

  const canChangePrimaryWebsite = !story?._id
    ? true // always allow during story creation
    : hasPermission(
        window.userRolesAuth.story.editPrimaryWebsite,
        story?.canonical_website
      )

  const isOrgSingleSite = Object.keys(websites).length === 1

  const { options, tooltipMessage } = React.useMemo(() => {
    let options: OptionType[] = []
    let tooltipMessage = ''

    if (isEditingTemplate) {
      options = generateOptions(websites)
    } else if (!story?._id) {
      options = generateOptions(websites, 'CRES')
      tooltipMessage = intl.get('PrimaryWebsiteField.createStoryFilterMessage')
    } else {
      options = generateOptions(websites, 'EDPW')
      tooltipMessage = intl.get(
        'PrimaryWebsiteField.editPrimaryWebsiteFilterMessage'
      )
    }

    return { options, tooltipMessage }
  }, [isEditingTemplate, story?._id, websites])

  React.useEffect(() => {
    // COMP-4467: Handle single site orgs creating a story from an existing template missing a primary website
    if (isOrgSingleSite && !story?.canonical_website) {
      dispatch(
        setStoryAns({
          storyAns: {
            ...story,
            canonical_website: options[0].value,
          },
        })
      )
    }
  }, [dispatch, isOrgSingleSite, options, story])

  return (
    <>
      <FormGroup className={styles.container}>
        <Label htmlFor="canonical_website">
          {intl.get('circulation.tab.primaryWebsiteLabel')}
          {!isEditingTemplate && (
            <i
              style={{ marginLeft: '5px' }}
              className="fa fa-info-circle"
              data-toggle="tooltip"
              data-placement="right"
              data-original-title={tooltipMessage}
              title={tooltipMessage}
            />
          )}
          <span className="text-danger"> ✱</span>
        </Label>
        <Select
          name="canonical_website"
          id="primary-website-select"
          inputId="canonical_website"
          isDisabled={
            isOrgSingleSite || isStoryLocked || !canChangePrimaryWebsite
          }
          selectProps={{ canChangePrimaryWebsite }}
          components={{ SelectContainer }}
          options={options}
          value={{
            label: websites[story?.canonical_website ?? '']?.display_name ?? '',
            value: story?.canonical_website ?? '',
          }}
          onChange={async (selectedOption: ValueType<OptionType>) => {
            dispatch(
              setStoryAns({
                storyAns: {
                  ...story,
                  canonical_website: (selectedOption as OptionType).value,
                  isDirty: true,
                },
                isDirty: true,
              })
            )
          }}
        />
      </FormGroup>

      <PaywallStatusConfirmModal />
    </>
  )
}
